import React, { useState } from "react";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "../lib/api";
import { toast } from "sonner";
import PhotoSyncPanel from "./PhotoSyncPanel";
import PaymentPanel from "./PaymentPanel";
import CourseSchedulePanel from "./CourseSchedulePanel";
import Timeline from "./Timeline";
import { X, CheckCircle, XCircle, Camera as CameraIcon, IdentificationCard, FileArrowDown, MagicWand, FilePdf, ChartLineUp, Copy } from "@phosphor-icons/react";

export default function RegistrationDialog({ registration, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [reg, setReg] = useState(registration);
  const [ocrIdx, setOcrIdx] = useState(null);

  const runOcr = async (idx) => {
    setOcrIdx(idx);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/documents/${idx}/ocr`);
      setReg(data);
      toast.success("Text extracted");
      // Note: intentionally NOT calling onChanged — OCR is metadata-only and
      // refetching the parent list can unmount this dialog before the user
      // sees the extracted text. Local setReg(data) keeps the panel fresh.
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "OCR failed");
    } finally {
      setOcrIdx(null);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/approve`);
      toast.success(`Approved · Student ID ${data.student_id}`);
      setReg(data);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/reject`, { reason });
      toast.success("Application rejected");
      setReg(data);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Rejection failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-900 shadow-2xl w-full max-w-3xl my-8" data-testid="reg-dialog">
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-slate-100 border border-slate-200 overflow-hidden shrink-0">
              {reg.photo_base64 ? (
                <img src={reg.photo_base64} alt={reg.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <IdentificationCard size={24} weight="duotone" />
                </div>
              )}
            </div>
            <div>
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">Application</div>
              <div className="font-display text-2xl font-black tracking-tighter mt-1">{reg.full_name}</div>
              <div className="text-sm text-slate-500">{reg.phone} · {reg.gender} · Age {reg.age}</div>
              <div className="mt-2 flex items-center gap-2">
                <StatusBadge status={reg.status} />
                {reg.student_id && (
                  <span className="font-mono-sms text-[11px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[var(--sms-primary)]/5 text-[var(--sms-primary)] border border-[var(--sms-primary)]/20" data-testid="dialog-student-id">
                    {reg.student_id}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="reg-dialog-close" className="text-slate-400 hover:text-slate-900">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          <Section title="Personal">
            <Row label="Age" value={reg.age} />
            <Row label="Gender" value={reg.gender} />
            <Row label="Phone" value={reg.phone} />
            <Row label="Blood group" value={reg.blood_group} />
          </Section>
          <Section title="Addresses">
            <Row label="Local" value={reg.local_address} />
            <Row label="Permanent" value={reg.same_as_local ? `${reg.permanent_address} (same as local)` : reg.permanent_address} />
          </Section>
          <Section title="Education">
            <Row label="College" value={reg.current_college || "—"} />
            <Row label="Semester" value={reg.current_semester || "—"} />
            <Row label="Status" value={reg.applicant_status} />
            <div className="text-sm mt-2 whitespace-pre-wrap text-slate-700">{reg.education_qualifications || "—"}</div>
          </Section>
          <Section title="Course & payment">
            <Row label="Programme" value={reg.course_name || "—"} />
            <Row label="Fee" value={reg.payment_amount != null ? `₹ ${Number(reg.payment_amount).toLocaleString()}` : "—"} />
            <Row label="Submitted" value={new Date(reg.submitted_at).toLocaleString()} />
            {reg.decided_at && <Row label="Decided" value={new Date(reg.decided_at).toLocaleString()} />}
          </Section>
          <Section title="Guardian">
            <Row label="Name" value={reg.guardian_name} />
            <Row label="Phone" value={reg.guardian_phone} />
          </Section>
          <Section title="Emergency contact">
            <Row label="Name" value={reg.emergency_name} />
            <Row label="Phone" value={reg.emergency_phone} />
          </Section>
          <Section title="Signature">
            {reg.signature_base64 ? (
              <div className="bg-white border border-slate-300 p-2 inline-block">
                <img src={reg.signature_base64} alt="signature" className="max-h-24" data-testid="dialog-signature" />
              </div>
            ) : (
              <div className="text-sm text-slate-400">Not provided</div>
            )}
          </Section>
          <Section title="Decision">
            {reg.rejection_reason ? (
              <div className="p-3 bg-red-50 border border-red-200">
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-red-600 mb-1">Rejection reason</div>
                <div className="text-sm text-red-900">{reg.rejection_reason}</div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">{reg.status === "approved" ? "Approved." : reg.status === "pending" ? "Awaiting review." : "—"}</div>
            )}
          </Section>
        </div>

        {/* Documents */}
        {reg.documents && reg.documents.length > 0 && (
          <div className="px-6 pb-6">
            <div className="border border-slate-200 p-5">
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">
                Submitted documents ({reg.documents.length})
              </div>
              <ul className="space-y-2">
                {reg.documents.map((d, i) => (
                  <li key={i} className="px-3 py-2 border border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-3">
                      <FileArrowDown size={16} weight="duotone" className="text-[var(--sms-primary)] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{d.name}</div>
                        <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">{d.mime_type}</div>
                      </div>
                      {((d.mime_type || "").startsWith("image/") || (d.mime_type || "").includes("pdf") || (d.name || "").toLowerCase().endsWith(".pdf")) && (
                        <button
                          onClick={() => runOcr(i)}
                          disabled={ocrIdx === i}
                          data-testid={`btn-ocr-${i}`}
                          className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-[var(--sms-primary)] hover:underline inline-flex items-center gap-1"
                        >
                          <MagicWand size={12} weight="bold" />
                          {ocrIdx === i
                            ? "OCRing…"
                            : d.ocr_status === "failed"
                            ? "Retry OCR"
                            : d.ocr_text ? "Re-extract" : "Extract text"}
                        </button>
                      )}
                      {d.ocr_status === "failed" && (
                        <span
                          data-testid={`ocr-failed-chip-${i}`}
                          title={d.ocr_error || "OCR extraction failed"}
                          className="font-mono-sms text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 bg-red-50 border border-red-200 text-red-700"
                        >
                          OCR failed
                        </span>
                      )}
                      <a href={d.base64} download={d.name} data-testid={`doc-download-${i}`} className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:text-[var(--sms-primary)]">
                        Download
                      </a>
                    </div>
                    {d.ocr_status === "failed" && d.ocr_error && (
                      <div className="mt-2 pt-2 border-t border-red-100 text-xs text-red-700" data-testid={`ocr-error-${i}`}>
                        <span className="font-mono-sms text-[10px] uppercase tracking-[0.15em]">Last error:</span>{" "}
                        {d.ocr_error}
                      </div>
                    )}
                    {d.ocr_text && (
                      <div className="mt-2 pt-2 border-t border-slate-200" data-testid={`ocr-result-${i}`}>
                        {d.ocr_summary && (
                          <div className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">{d.ocr_summary}</div>
                        )}
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans max-h-40 overflow-y-auto sms-scroll">{d.ocr_text}</pre>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Payment panel — only for approved/enrolled */}
        {(reg.status === "approved" || reg.status === "enrolled") && (
          <div className="px-6 pb-6">
            <PaymentPanel
              registration={reg}
              onChanged={(updated) => {
                setReg(updated);
                onChanged?.();
              }}
            />
          </div>
        )}

        {/* Course schedule — staff records start date once student begins */}
        {reg.status === "enrolled" && (
          <div className="px-6 pb-6">
            <CourseSchedulePanel
              registration={reg}
              onSaved={(updated) => {
                setReg(updated);
                onChanged?.();
              }}
            />
          </div>
        )}

        {/* Timeline */}
        <div className="px-6 pb-6">
          <Timeline registration={reg} />
        </div>

        {showSync && (
          <div className="px-6 pb-6">
            <PhotoSyncPanel
              registrationId={reg.id}
              onAttached={(b64) => setReg((r) => ({ ...r, photo_base64: b64 }))}
              onClose={() => setShowSync(false)}
            />
          </div>
        )}

        {rejectMode && (
          <div className="px-6 pb-6">
            <div className="border border-red-200 bg-red-50/30 p-4">
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-red-600 mb-2">Rejection reason</div>
              <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} className="sms-input" data-testid="reject-reason" placeholder="Explain why this application is being rejected (optional)" />
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setRejectMode(false)} className="sms-btn-secondary text-xs">Cancel</button>
                <button onClick={reject} disabled={busy} data-testid="confirm-reject" className="sms-btn-danger">Confirm rejection</button>
              </div>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="border-t border-slate-200 px-6 py-4 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSync((v) => !v)}
              data-testid="btn-toggle-photo-sync"
              className="sms-btn-secondary inline-flex items-center gap-2"
            >
              <CameraIcon size={14} weight="bold" />
              {showSync ? "Hide photo sync" : "Capture photo"}
            </button>
            <button
              onClick={async () => {
                let token = reg.form_token;
                if (!token) {
                  // Older registration — backfill via admin endpoint
                  try {
                    const { data } = await api.post(`/registrations/${reg.id}/regenerate-form-token`);
                    setReg(data);
                    token = data.form_token;
                  } catch (e) {
                    toast.error("Could not generate tracking link");
                    return;
                  }
                }
                const url = `${window.location.origin}/track/${token}`;
                await navigator.clipboard.writeText(url);
                toast.success("Tracking link copied — share with the student");
              }}
              data-testid="btn-copy-tracking-link"
              className="sms-btn-secondary inline-flex items-center gap-2"
            >
              <ChartLineUp size={14} weight="duotone" /> <Copy size={11} weight="bold" /> Tracking link
            </button>
            <button
              onClick={async () => {
                const token = localStorage.getItem(TOKEN_KEY);
                const res = await fetch(`${BACKEND_URL}/api/registrations/${reg.id}/form-pdf`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) {
                  toast.error("PDF generation failed");
                  return;
                }
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              }}
              data-testid="btn-print-application"
              className="sms-btn-secondary inline-flex items-center gap-2"
            >
              <FilePdf size={14} weight="duotone" /> Print application
            </button>
          </div>
          <div className="flex items-center gap-2">
            {reg.status === "pending" && (
              <>
                <button onClick={() => setRejectMode(true)} disabled={busy} data-testid="btn-reject" className="sms-btn-danger inline-flex items-center gap-2">
                  <XCircle size={14} weight="bold" /> Reject
                </button>
                <button onClick={approve} disabled={busy} data-testid="btn-approve" className="sms-btn-primary inline-flex items-center gap-2">
                  <CheckCircle size={14} weight="bold" /> {busy ? "Approving…" : "Approve & generate ID"}
                </button>
              </>
            )}
            {reg.status !== "pending" && (
              <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Decision finalised
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-[var(--sms-primary)] mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="text-sm">
      <span className="font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500 mr-2">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = status === "enrolled" ? "sms-badge-approved" : status === "approved" ? "sms-badge-pending" : status === "rejected" ? "sms-badge-rejected" : "sms-badge-pending";
  return <span className={`sms-badge ${cls}`}>{status}</span>;
}
